import React, { useState, useRef, useEffect } from 'react';
import {
    FaTrashAlt, FaFileAlt,
    FaFolderOpen, FaCalendarAlt, FaHistory,
    FaEdit, FaCloudUploadAlt, FaCheckCircle, FaShieldAlt, FaInfoCircle,
    FaPlusCircle, FaSyncAlt, FaCopy, FaEyeSlash, FaUnlock
} from 'react-icons/fa';
import { Modal, CloseButton, Form, Button, ProgressBar, Badge, Card, Row, Col, Nav } from 'react-bootstrap';
import {
    getFirestore, doc, updateDoc, deleteDoc, arrayUnion, arrayRemove,
    collection, query, orderBy, getDocs, addDoc, getDoc, where
} from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-toastify';
import { spinner } from "../utils/Helper";

export const getCleanFileName = (url) => {
    if (!url) return "Attachment";
    try {
        const decodedUrl = decodeURIComponent(url);
        const fileNameWithParams = decodedUrl.split('/').pop();
        const fullFileName = fileNameWithParams.split('?')[0];
        const parts = fullFileName.split('_');
        return parts.length > 2 ? parts.slice(2).join('_') : fullFileName;
    } catch (error) { return "View Attachment"; }
};

const getFileIcon = (url) => {
    const name = getCleanFileName(url).toLowerCase();
    if (name.endsWith('.pdf')) return <FaFileAlt className="text-danger" />;
    return <FaFileAlt className="text-info" />;
};

const getLogMetadata = (action) => {
    const act = action.toLowerCase();
    if (act.includes('created') || act.includes('logged')) return { icon: <FaPlusCircle />, color: '#00FF88' };
    if (act.includes('updated') || act.includes('changed')) return { icon: <FaSyncAlt />, color: '#FFD700' };
    if (act.includes('reactivated') || act.includes('flagged') || act.includes('active')) return { icon: <FaShieldAlt />, color: '#FF4D4D' };
    if (act.includes('cleared')) return { icon: <FaCheckCircle />, color: '#00D9FF' };
    if (act.includes('added') || act.includes('attachment')) return { icon: <FaCloudUploadAlt />, color: '#FFFFFF' };
    if (act.includes('removed') || act.includes('deleted')) return { icon: <FaTrashAlt />, color: '#FF4D4D' };
    return { icon: <FaHistory />, color: '#FFFFFF' };
};

export function IncidentForm({ mode = "create", item, theme, show, onHide, onSuccess }) {
    const isDark = theme === 'dark';
    const db = getFirestore();
    const storage = getStorage();
    const fileInputRef = useRef(null);

    const [incidentId, setIncidentId] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [justSaved, setJustSaved] = useState(false);
    const [progress, setProgress] = useState(0);
    const [profiles, setProfiles] = useState([]);
    const [targetProfile, setTargetProfile] = useState(null);
    const [activeTab, setActiveTab] = useState('details');

    const initialFormState = {
        name: '', details: '', assignedTo: '', assignedEmail: '', profileId: '', incidentDate: '', isPrivate: false, fileURLs: [], activityLog: [], status: 'Cleared'
    };

    const [formData, setFormData] = useState(initialFormState);

    useEffect(() => {
        if (show) {
            if (mode === "create") {
                setFormData(initialFormState);
                setIncidentId(null);
                setJustSaved(false);
                setActiveTab('details');
            } else if (item) {
                setFormData(item);
                setIncidentId(item.id);
            }
        }
    }, [show, mode, item]);

    useEffect(() => {
        const fetchProfiles = async () => {
            try {
                const q = query(collection(db, "profiles"), orderBy("name", "asc"));
                const querySnapshot = await getDocs(q);
                const profileData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setProfiles(profileData);

                const currentId = formData.profileId;
                if (currentId) {
                    const found = profileData.find(p => p.id === currentId);
                    if (found) setTargetProfile(found);
                }
            } catch (err) { console.error("Error fetching profiles:", err); }
        };
        if (show) fetchProfiles();
    }, [show, db, formData.profileId]);

    const syncProfileStanding = async (pId) => {
        const incidentRef = collection(db, "incidentz");
        const q = query(incidentRef, where("profileId", "==", pId));
        const snap = await getDocs(q);
        const hasAnyActive = snap.docs.some(d => d.data().status === 'Active');
        await updateDoc(doc(db, "profiles", pId), { hasIncident: hasAnyActive });
        return hasAnyActive;
    };

    const handleSaveIncident = async () => {
        if (!formData.name.trim() || !formData.profileId) return toast.error("Select a Subject and enter a Title");
        setIsSaving(true);
        const user = getAuth().currentUser;
        const adminName = user?.displayName || user?.email || "Admin";
        const timestamp = new Date().toISOString();

        try {
            let actionText = mode === 'create' ? "Incident Logged" : "Report Details Updated";
            const historyEntry = { action: actionText, admin: adminName, date: timestamp };

            const cleanData = {
                name: formData.name,
                details: formData.details,
                assignedTo: formData.assignedTo,
                assignedEmail: formData.assignedEmail,
                profileId: formData.profileId,
                incidentDate: formData.incidentDate,
                isPrivate: formData.isPrivate,
                updated: new Date(),
                lastModifiedBy: adminName,
                activityLog: arrayUnion(historyEntry)
            };

            if (incidentId) {
                await updateDoc(doc(db, "incidentz", incidentId), cleanData);
                toast.success("Record Synced");
            } else {
                const docRef = await addDoc(collection(db, "incidentz"), {
                    ...cleanData,
                    fileURLs: [],
                    status: 'Cleared',
                    createdBy: adminName,
                    created: new Date()
                });
                setIncidentId(docRef.id);
                toast.success("Incident Logged (Inactive)");
            }
            setJustSaved(true);
            if (onSuccess) onSuccess();
        } catch (err) { toast.error("Database Error"); }
        finally { setIsSaving(false); }
    };

    const toggleStanding = async (newStatus) => {
        if (!incidentId || !formData.profileId) return;
        const admin = getAuth().currentUser?.email || "Admin";
        spinner(true);
        try {
            const logEntry = { action: `Incident marked: ${newStatus}`, admin, date: new Date().toISOString() };
            await updateDoc(doc(db, "incidentz", incidentId), { status: newStatus, activityLog: arrayUnion(logEntry) });
            const isNowInViolation = await syncProfileStanding(formData.profileId);
            setFormData(prev => ({ ...prev, status: newStatus, activityLog: [...(prev.activityLog || []), logEntry] }));
            setTargetProfile(prev => ({ ...prev, hasIncident: isNowInViolation }));
            toast.success(`Record marked ${newStatus}`);
            if (onSuccess) onSuccess();
        } catch (err) { toast.error("Update failed"); }
        finally { spinner(false); }
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file || !incidentId) return;
        const admin = getAuth().currentUser?.email || "Admin";
        const fileRef = ref(storage, `incidentz/${incidentId}/${file.name}`);
        const uploadTask = uploadBytesResumable(fileRef, file);

        uploadTask.on('state_changed',
            (snap) => setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
            null,
            async () => {
                const url = await getDownloadURL(uploadTask.snapshot.ref);
                const logEntry = { action: `Evidence Added: ${file.name}`, admin, date: new Date().toISOString() };
                await updateDoc(doc(db, "incidentz", incidentId), { fileURLs: arrayUnion(url), activityLog: arrayUnion(logEntry) });
                setFormData(prev => ({ ...prev, fileURLs: [...(prev.fileURLs || []), url], activityLog: [...(prev.activityLog || []), logEntry] }));
                setProgress(0);
                setJustSaved(false);
            }
        );
    };

    const removeFile = async (url) => {
        if (!window.confirm("Delete this attachment?")) return;
        const admin = getAuth().currentUser?.email || "Admin";
        const fileName = getCleanFileName(url);
        try {
            const logEntry = { action: `Evidence Removed: ${fileName}`, admin, date: new Date().toISOString() };
            await deleteObject(ref(storage, url)).catch(() => { });
            await updateDoc(doc(db, "incidentz", incidentId), { fileURLs: arrayRemove(url), activityLog: arrayUnion(logEntry) });
            setFormData(prev => ({ ...prev, fileURLs: prev.fileURLs.filter(u => u !== url), activityLog: [...(prev.activityLog || []), logEntry] }));
            toast.success("Attachment deleted");
        } catch (err) { toast.error("Delete failed"); }
    };

    const styles = {
        input: isDark ? 'glass-input mb-3 w-100' : 'form-control mb-3 w-100 shadow-sm',
        label: "text-uppercase small text-info fw-bold opacity-75 mb-1 d-block"
    };

    return (
        <Modal
            show={show} onHide={onHide} size="lg" centered backdrop="static"
            backdropClassName={isDark ? "adminBackdrop" : "docLight "}
            contentClassName={isDark ? "bg-dark text-white  shadow-lg" : "xlight  shadow-lg"}
        >
            <Modal.Header closeButton closeVariant={isDark ? "white" : undefined} className="border-0 pb-0">
                <Modal.Title className={`fw-bold opacity-75 ${isDark ? 'text-white' : 'text-dark'}`}>
                    {mode === 'create' ? 'Log New Incident' : 'Update Incident Report'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="px-4 mt-3 mb-2">
                    <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="border-bottom-0 d-flex flex-row">
                        <Nav.Item className="flex-fill">
                            <Nav.Link
                                eventKey="details"
                                className={`px-4 py-2 fw-bold text-center border-0 ${activeTab === 'details' ? 'bg-primary text-white shadow-sm' : isDark ? 'text-white-50' : 'text-dark-50'}`}
                                style={{ borderRadius: '4px' }}
                            >
                                <FaInfoCircle className="me-2" /> DETAILS
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item className="flex-fill ms-2">
                            <Nav.Link
                                eventKey="history"
                                className={`px-4 py-2 fw-bold text-center border-0 ${activeTab === 'history' ? 'bg-primary text-white shadow-sm' : isDark ? 'text-white-50' : 'text-dark-50'}`}
                                style={{ borderRadius: '4px' }}
                            >
                                <FaHistory className="me-2" /> AUDIT HISTORY
                            </Nav.Link>
                        </Nav.Item>
                    </Nav>
                </div>
                <hr></hr>
                {activeTab === 'details' ? (
                    <div className="p-4 pt-2">
                        <Row className="g-4">
                            <Col lg={7}>
                                <label className={styles.label}>Subject</label>
                                <select className={styles.input} value={formData.profileId} onChange={e => {
                                    const p = profiles.find(x => x.id === e.target.value);
                                    setFormData({ ...formData, profileId: e.target.value, assignedEmail: p?.email || '', assignedTo: p?.name || '' });
                                }}>
                                    <option value="">Select a member...</option>
                                    {profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                                <label className={styles.label}>Title</label><input type="text" className={styles.input} value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
                                <label className={styles.label}>Date</label><input type="date" className={styles.input} value={formData.incidentDate} onChange={e => setFormData({ ...formData, incidentDate: e.target.value })} />
                                <label className={styles.label}>Facts</label><textarea className={styles.input} rows="6" value={formData.details} onChange={e => setFormData({ ...formData, details: e.target.value })} />
                                <Form.Check type="switch" label="Admin Only (Hide from Subject Access)" className={`mb-4 fw-bold ${isDark ? 'text-white-50' : 'text-dark-50'}`} checked={formData.isPrivate} onChange={e => setFormData({ ...formData, isPrivate: e.target.checked })} />
                            </Col>
                            <Col lg={5} className={`border-start-lg border-opacity-25 ${isDark ? '' : 'border-dark'}`}>
                                {incidentId && (
                                    <>
                                        <label className={styles.label}>Standing Management</label>
                                        <Card className={`mb-4 border-opacity-25 shadow-sm ${formData.status === 'Active' ? 'border-danger bg-danger bg-opacity-10' : 'border-success bg-success bg-opacity-10'}`}>
                                            <Card.Body className="p-3">
                                                <div className={`fw-bold mb-2 ${formData.status === 'Active' ? 'text-danger' : 'text-success'}`}>RECORD IS {formData.status?.toUpperCase()}</div>
                                                <Button variant={formData.status === 'Active' ? "success" : "danger"} size="sm" className="w-100 fw-bold" onClick={() => toggleStanding(formData.status === 'Active' ? 'Cleared' : 'Active')}>
                                                    {formData.status === 'Active' ? 'Mark Cleared' : 'Activate Violation'}
                                                </Button>
                                                <div className="x-small mt-2 opacity-50 text-white italic">Note: Subject standing clears only when ALL records are cleared.</div>
                                            </Card.Body>
                                        </Card>

                                        <label className={styles.label}>Evidence</label>
                                        <div className={`p-3 rounded  ${isDark ? 'bg-black bg-opacity-25 ' : 'bg-white border-dark'}`}>
                                            {formData.fileURLs?.map((url, i) => (
                                                <div key={i} className={`d-flex justify-content-between mb-2 p-2 rounded  align-items-center ${isDark ? ' ' : 'bg-light border-dark'}`}>
                                                    <span className={`small text-truncate me-2 fw-bold ${isDark ? 'text-white' : 'text-dark'}`}>{getFileIcon(url)} {getCleanFileName(url)}</span>
                                                    <button className="btn btn-sm text-danger p-0" onClick={() => removeFile(url)}><FaTrashAlt size={12} /></button>
                                                </div>
                                            ))}
                                            {progress > 0 && <ProgressBar now={progress} variant="danger" className="mb-2" style={{ height: '4px' }} />}
                                            <input type="file" className="d-none" ref={fileInputRef} onChange={handleFileUpload} />
                                            <Button variant="outline-dark" size="sm" className="w-100 fw-bold opacity-50" onClick={() => fileInputRef.current.click()}>Upload Evidence</Button>
                                        </div>
                                    </>
                                )}
                                {!incidentId && (
                                    <div className="text-center py-5 opacity-50 italic small">Save record details to enable evidence uploads and standing management.</div>
                                )}
                            </Col>
                        </Row>
                    </div>
                ) : (
                    <div>
                        <div className={`rounded p-3  ${isDark ? 'bg-black  ' : 'bg-white border-dark'}`} style={{ minHeight: '300px' }}>
                            {formData.activityLog?.slice().reverse().map((log, i) => {
                                const meta = getLogMetadata(log.action);
                                return (
                                    <div key={i} className={`py-3 px-2 border-bottom border-opacity-10 d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-sm-between gap-3 ${isDark ? '' : 'border-dark'}`}>
                                        <div className="d-flex align-items-center gap-3">
                                            <span style={{ color: meta.color, fontSize: '1.2rem' }} className="flex-shrink-0">{meta.icon}</span>
                                            <div className="d-flex flex-column">
                                                <span className={`fw-bold ${isDark ? 'text-white' : 'text-dark'}`} style={{ fontSize: '1.05rem', lineHeight: '1.2' }}>{log.action}</span>
                                                <span className="text-info small fw-bold">Admin: {log.admin}</span>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center justify-content-between justify-content-sm-end w-100 w-sm-auto gap-3">
                                            <div className="d-flex flex-column text-start text-sm-end">
                                                <span className={`small fw-bold ${isDark ? 'text-white' : 'text-dark'}`}>{new Date(log.date).toLocaleDateString()}</span>
                                                <span className={`x-small fw-bold ${isDark ? 'text-white-50' : 'text-muted'}`}>{new Date(log.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                            </div>
                                            <button className={`btn btn-sm btn-link p-0 ${isDark ? 'text-white-50' : 'text-muted'}`} onClick={() => navigator.clipboard.writeText(`[${new Date(log.date).toLocaleDateString()}] ${log.action} - Admin: ${log.admin}`)} title="Copy Audit Entry">
                                                <FaCopy size={16} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer >
                <Button variant="secondary" onClick={onHide}>Close</Button>
                <Button variant={justSaved ? "success white" : "info white"} onClick={handleSaveIncident} disabled={isSaving}>{isSaving ? "Syncing..." : "Save Changes"}</Button>
            </Modal.Footer>
        </Modal>
    );
}

export function CreateIncident({ theme, onSuccess }) {
    const [show, setShow] = useState(false);
    return (
        <>
            <button className="btn-glass-create bg-danger border-0 fw-bold" onClick={() => setShow(true)}>+ Report Incident</button>
            <IncidentForm mode="create" theme={theme} show={show} onHide={() => setShow(false)} onSuccess={onSuccess} />
        </>
    );
}

const IncidentzList = (props) => {
    const { theme = 'dark' } = props;
    const isDark = theme === 'dark';
    const [showSummary, setShowSummary] = useState(false);
    const [showUpdate, setShowUpdate] = useState(false);
    const [profileStatus, setProfileStatus] = useState(null);

    const fetchStatus = async () => {
        if (props.profileId) {
            const db = getFirestore();
            const pSnap = await getDoc(doc(db, "profiles", props.profileId));
            if (pSnap.exists()) {
                setProfileStatus(pSnap.data().hasIncident);
            }
        }
    };

    useEffect(() => {
        if (showSummary || showUpdate || profileStatus === null) fetchStatus();
    }, [props.profileId, showUpdate, showSummary, profileStatus]);

    const handleUpdateSuccess = () => {
        fetchStatus();
        if (props.onDeleteSuccess) props.onDeleteSuccess();
    };

    const handleDelete = async () => {
        if (!window.confirm("Delete this incident permanently?")) return;
        spinner(true);
        const db = getFirestore();
        const storage = getStorage();
        try {
            if (props.fileURLs && props.fileURLs.length > 0) {
                for (const url of props.fileURLs) {
                    await deleteObject(ref(storage, url)).catch(() => { });
                }
            }
            await deleteDoc(doc(db, "incidentz", props.id));
            const incidentRef = collection(db, "incidentz");
            const q = query(incidentRef, where("profileId", "==", props.profileId));
            const snap = await getDocs(q);
            const hasAnyActive = snap.docs.some(d => d.data().status === 'Active');
            await updateDoc(doc(db, "profiles", props.profileId), { hasIncident: hasAnyActive });
            toast.success("Incident removed");
            if (props.onDeleteSuccess) props.onDeleteSuccess();
        } catch (err) {
            toast.error("Delete failed");
        } finally {
            spinner(false);
        }
    };

    return (
        <div className={`doc-card mb-3 p-3 shadow-sm ${isDark ? 'bg-dark text-white ' : 'bg-white text-dark border-light'}`} style={{ borderLeft: '5px solid #dc3545' }}>
            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3">
                <div onClick={() => setShowSummary(true)} style={{ cursor: 'pointer' }} className="w-100">
                    <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                        <h6 className={`m-0 fw-bold ${isDark ? 'text-white' : 'text-dark'}`}>{props.name}</h6>

                        {!props.public && (
                            <>
                                {props.isPrivate ? (
                                    <Badge bg="warning" text="dark" className="x-small fw-bold"><FaEyeSlash className="me-1" /> ADMIN ONLY</Badge>
                                ) : (
                                    <Badge bg="primary" className="x-small fw-bold"><FaUnlock className="me-1" /> SUBJECT ACCESS</Badge>
                                )}
                            </>
                        )}

                        <Badge bg={props.status === 'Active' ? "danger" : "success"} className="x-small fw-bold">RECORD: {props.status?.toUpperCase() || 'ACTIVE'}</Badge>

                        {profileStatus === true && props.status === 'Active' && (
                            <Badge bg="warning" text="dark" className="x-small animate-pulse fw-bold">SUBJECT IN VIOLATION</Badge>
                        )}
                        {profileStatus === false && (
                            <Badge bg="success" className="x-small fw-bold">SUBJECT CLEARED</Badge>
                        )}
                    </div>
                    <div className="text-danger small fw-bold">{props.assignedTo}</div>
                    <div className={`${isDark ? 'text-muted' : 'text-secondary'} small`}><FaCalendarAlt size={10} /> {props.incidentDate}</div>
                </div>
                <div className="d-flex gap-2 ms-auto ms-sm-0">
                    <button onClick={() => setShowSummary(true)} className="circle-btn border-secondary orange shadow-sm"><FaFolderOpen size={12} /></button>
                    {!props.public && (
                        <>
                            <button onClick={() => setShowUpdate(true)} className="circle-btn  text-white shadow-sm"><FaEdit size={12} /></button>
                            <button onClick={handleDelete} className="circle-btn bg-danger text-white shadow-sm"><FaTrashAlt size={12} /></button>
                        </>
                    )}
                </div>
            </div>

            <Modal
                show={showSummary} onHide={() => setShowSummary(false)} centered
                backdropClassName={isDark ? "adminBackdrop" : "docLight border-start"}
                contentClassName={isDark ? "bg-dark text-white  shadow-lg" : " text-dark shadow-lg"}
            >
                <Modal.Body className="p-4">
                    <div className="d-flex justify-content-between mb-3">
                        <Badge bg="danger" className="fw-bold">OFFICIAL RECORD</Badge>
                        <div className={`${isDark ? 'text-muted' : 'text-secondary'} small fw-bold`}>REF: {props.id?.slice(-6).toUpperCase()}</div>
                    </div>
                    <h3 className={`fw-bold mb-1 ${isDark ? 'text-white' : 'text-dark'}`}>{props.name}</h3>
                    <div className="d-flex gap-2 flex-wrap mb-4">
                        <span className={`${isDark ? 'text-muted' : 'text-secondary'} small fw-bold`}>{props.assignedTo} | {props.incidentDate}</span>
                        <Badge bg={props.status === 'Active' ? "danger" : "success"}>RECORD: {props.status?.toUpperCase()}</Badge>
                        {profileStatus === true && props.status === 'Active' && (
                            <Badge bg="warning" text="dark">SUBJECT: IN VIOLATION</Badge>
                        )}
                        {profileStatus === false && (
                            <Badge bg="success">SUBJECT: CLEARED</Badge>
                        )}
                    </div>
                    <label className={`text-uppercase x-small fw-bold mb-2 d-block ${isDark ? 'text-info' : 'text-primary'}`}>Report Details</label>
                    <p className={`p-3 rounded border border-info border-opacity-10 ${isDark ? 'bg-black bg-opacity-10 text-white ' : 'bg-white text-dark border-dark'}`} style={{ whiteSpace: 'pre-wrap' }}>{props.details || "No details provided."}</p>
                    <label className={`text-uppercase x-small fw-bold mb-2 d-block ${isDark ? 'text-info' : 'text-primary'}`}>Evidence</label>
                    <div className={`rounded border border-secondary border-opacity-25 mb-4 ${isDark ? '' : 'border-dark'}`}>
                        {props.fileURLs?.length > 0 ? props.fileURLs.map((url, i) => (
                            <div key={i} className={`d-flex justify-content-between p-2 border-bottom border-opacity-10 align-items-center ${isDark ? 'bg-black bg-opacity-20' : 'bg-white'}`}>
                                <span className={`small text-truncate fw-bold ${isDark ? 'text-white' : 'text-dark'}`}>{getFileIcon(url)} {getCleanFileName(url)}</span>
                                <a href={url} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-info py-0 fw-bold">View</a>
                            </div>
                        )) : <div className="p-3 text-center small opacity-50">No evidence attached.</div>}
                    </div>
                    <Button variant="info" className="w-100 fw-bold shadow-sm" onClick={() => setShowSummary(false)}>Close Report</Button>
                </Modal.Body>
            </Modal>
            <IncidentForm mode="update" item={props} theme={theme} show={showUpdate} onHide={() => setShowUpdate(false)} onSuccess={handleUpdateSuccess} />
        </div>
    );
};

export default IncidentzList;